import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import AdminSidebar from '../../components/admin/AdminSidebar';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { 
  Search, Filter, RefreshCw, Loader2, Eye, X, AlertTriangle,
  Calendar, MapPin, User, CreditCard, Truck, Phone, Clock, FileText,
  CheckCircle, XCircle, AlertCircle, Trash2, Plane, Building, ExternalLink, Package
} from 'lucide-react';
import { toast } from 'sonner';
import { 
  getPickupDisplayLabel, 
  getDropoffDisplayLabel, 
  getPickupSourceLabel,
  getCoordinates,
  getGoogleMapsUrl,
  getGoogleMapsDirectionsUrl,
  fromLegacy
} from '../../utils/legacyCompat';

const API_BASE = window.location.origin || '';

// Cancel reason options
const CANCEL_REASONS = [
  { id: 'customer_request', label: 'Customer Request' },
  { id: 'duplicate', label: 'Duplicate Booking' },
  { id: 'fraud', label: 'Fraud/Suspicious' },
  { id: 'no_show', label: 'No Show' },
  { id: 'provider_issue', label: 'Provider Issue' },
  { id: 'other', label: 'Other' }
];

const AdminBookingsPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [bookings, setBookings] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || '');
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [showDetail, setShowDetail] = useState(false);
  
  // Cancel modal state
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelReason, setCancelReason] = useState('customer_request');
  const [isCancelling, setIsCancelling] = useState(false);
  const [cancelError, setCancelError] = useState(null);
  
  // Cancel logs
  const [cancellationLogs, setCancellationLogs] = useState([]);
  const [canCancel, setCanCancel] = useState(true);
  const [cancelBlockedReason, setCancelBlockedReason] = useState(null);
  const [providerTripId, setProviderTripId] = useState(null);
  
  // Refund state
  const [refundLogs, setRefundLogs] = useState([]);
  const [canRefund, setCanRefund] = useState(false);
  const [refundBlockedReason, setRefundBlockedReason] = useState(null);
  const [isRefunding, setIsRefunding] = useState(false);
  
  // Status update state
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
  
  // Delete state
  const [selectedForDelete, setSelectedForDelete] = useState([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const getAuthHeader = () => {
    const token = localStorage.getItem('admin_token') || localStorage.getItem('vipgez_admin_token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const fetchBookings = async () => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('page', page);
      params.set('limit', 20);
      if (search) params.set('search', search);
      if (statusFilter) params.set('status', statusFilter);

      const response = await fetch(`${API_BASE}/api/admin/bookings?${params}`, {
        headers: { ...getAuthHeader(), 'Content-Type': 'application/json' }
      });

      if (response.status === 401) {
        navigate('/login-goturkeytransfer');
        return;
      }

      const data = await response.json();
      if (data.success) {
        setBookings(data.bookings || []);
        setTotal(data.total || 0);
        setTotalPages(data.totalPages || 1);
      }
    } catch (error) {
      console.error('Fetch bookings error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchBookingDetail = async (ref) => {
    try {
      const response = await fetch(`${API_BASE}/api/admin/bookings/${ref}`, {
        headers: { ...getAuthHeader(), 'Content-Type': 'application/json' }
      });
      const data = await response.json();
      if (data.success) {
        setSelectedBooking(data.booking);
        setCancellationLogs(data.cancellationLogs || []);
        setCanCancel(data.canCancel !== false);
        setCancelBlockedReason(data.cancelBlockedReason);
        setProviderTripId(data.providerTripId);
        setRefundLogs(data.refundLogs || []);
        setCanRefund(data.canRefund === true);
        setRefundBlockedReason(data.refundBlockedReason);
        setShowDetail(true);
      }
    } catch (error) {
      console.error('Fetch booking detail error:', error);
    }
  };

  const handleCancelBooking = async () => {
    if (!selectedBooking) return;
    
    setIsCancelling(true);
    setCancelError(null);
    
    try {
      const response = await fetch(`${API_BASE}/api/admin/bookings/${selectedBooking.booking_ref}/cancel`, {
        method: 'POST',
        headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          reason: cancelReason,
          actor: 'admin'
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        toast.success('Booking cancelled successfully');
        setShowCancelModal(false);
        fetchBookings();
        fetchBookingDetail(selectedBooking.booking_ref);
      } else {
        // Handle specific error cases
        if (data.error_code === 'PROVIDER_TRIP_ID_MISSING') {
          setCancelError('Provider trip ID missing. Cannot cancel with provider.');
        } else if (data.provider_code === 456) {
          setCancelError('Transfer has already started. Cannot cancel after pickup.');
        } else if (data.provider_code === 455) {
          setCancelError('Trip not found with provider. Provider trip ID may be incorrect.');
        } else if (data.can_retry) {
          setCancelError(`${data.message} Will retry automatically.`);
          toast.warning('Cancellation requested - will retry automatically');
          setShowCancelModal(false);
          fetchBookingDetail(selectedBooking.booking_ref);
        } else {
          setCancelError(data.message || 'Failed to cancel booking');
        }
      }
    } catch (error) {
      console.error('Cancel booking error:', error);
      setCancelError('Network error. Please try again.');
    } finally {
      setIsCancelling(false);
    }
  };

  const handleManualRefund = async () => {
    if (!selectedBooking) return;
    
    setIsRefunding(true);
    
    try {
      const response = await fetch(`${API_BASE}/api/admin/bookings/${selectedBooking.booking_ref}/refund`, {
        method: 'POST',
        headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason: 'manual_refund' })
      });
      
      const data = await response.json();
      
      if (data.success) {
        toast.success(`Refund processed successfully: ${data.refund_id || 'N/A'}`);
        fetchBookingDetail(selectedBooking.booking_ref);
      } else {
        toast.error(data.message || data.error || 'Failed to process refund');
      }
    } catch (error) {
      console.error('Manual refund error:', error);
      toast.error('Network error. Please try again.');
    } finally {
      setIsRefunding(false);
    }
  };

  const handleStatusUpdate = async (newStatus) => {
    if (!selectedBooking) return;
    
    setIsUpdatingStatus(true);
    
    try {
      const response = await fetch(`${API_BASE}/api/admin/bookings/${selectedBooking.booking_ref}/status?status=${newStatus}`, {
        method: 'POST',
        headers: { ...getAuthHeader(), 'Content-Type': 'application/json' }
      });
      
      const data = await response.json();
      
      if (data.success) {
        toast.success(`Status updated to ${newStatus}`);
        // Update local state
        setSelectedBooking(prev => ({ ...prev, status: newStatus, booking_status: newStatus }));
        // Refresh bookings list
        fetchBookings();
      } else {
        toast.error(data.detail || data.message || 'Failed to update status');
      }
    } catch (error) {
      console.error('Status update error:', error);
      toast.error('Network error. Please try again.');
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  // Delete functions
  const handleSelectForDelete = (bookingRef) => {
    setSelectedForDelete(prev => {
      if (prev.includes(bookingRef)) {
        return prev.filter(ref => ref !== bookingRef);
      } else {
        return [...prev, bookingRef];
      }
    });
  };

  const handleSelectAll = () => {
    if (selectedForDelete.length === bookings.length) {
      setSelectedForDelete([]);
    } else {
      setSelectedForDelete(bookings.map(b => b.booking_ref));
    }
  };

  const handleBulkDelete = async () => {
    if (selectedForDelete.length === 0) return;
    
    setIsDeleting(true);
    
    try {
      const response = await fetch(`${API_BASE}/api/admin/bookings/bulk`, {
        method: 'DELETE',
        headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ booking_refs: selectedForDelete })
      });
      
      const data = await response.json();
      
      if (data.success) {
        toast.success(`${data.deleted_count} booking(s) deleted successfully`);
        setSelectedForDelete([]);
        setShowDeleteModal(false);
        fetchBookings();
      } else {
        toast.error(data.detail || 'Failed to delete bookings');
      }
    } catch (error) {
      console.error('Bulk delete error:', error);
      toast.error('Network error. Please try again.');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleSingleDelete = async (bookingRef) => {
    setIsDeleting(true);
    
    try {
      const response = await fetch(`${API_BASE}/api/admin/bookings/${bookingRef}`, {
        method: 'DELETE',
        headers: { ...getAuthHeader(), 'Content-Type': 'application/json' }
      });
      
      const data = await response.json();
      
      if (data.success) {
        toast.success('Booking deleted successfully');
        setShowDetail(false);
        fetchBookings();
      } else {
        toast.error(data.detail || 'Failed to delete booking');
      }
    } catch (error) {
      console.error('Delete error:', error);
      toast.error('Network error. Please try again.');
    } finally {
      setIsDeleting(false);
    }
  };

  useEffect(() => {
    const token = localStorage.getItem('admin_token') || localStorage.getItem('vipgez_admin_token');
    if (!token) {
      navigate('/login-goturkeytransfer');
      return;
    }
    fetchBookings();
  }, [page, statusFilter]);

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    fetchBookings();
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'EUR' }).format(amount || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-US');
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('en-US', {
      dateStyle: 'short',
      timeStyle: 'short'
    });
  };

  const getStatusBadge = (status) => {
    const styles = {
      confirmed: 'bg-green-500/20 text-green-400 border-green-500/30',
      confirmed_cash: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
      pending: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
      pending_payment: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
      paid_confirmed: 'bg-green-500/20 text-green-400 border-green-500/30',
      cancel_requested: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
      cancelled: 'bg-red-500/20 text-red-400 border-red-500/30',
      cancellation_failed: 'bg-red-500/20 text-red-400 border-red-500/30',
      completed: 'bg-gray-500/20 text-gray-400 border-gray-500/30',
      refunded: 'bg-purple-500/20 text-purple-400 border-purple-500/30'
    };
    const labels = {
      confirmed: 'Confirmed',
      confirmed_cash: 'Cash Confirmed',
      pending: 'Pending',
      pending_payment: 'Pending Payment',
      paid_confirmed: 'Paid & Confirmed',
      cancel_requested: 'Cancel Requested',
      cancelled: 'Cancelled',
      cancellation_failed: 'Cancel Failed',
      completed: 'Completed',
      refunded: 'Refunded'
    };
    return (
      <span className={`text-xs px-2 py-1 rounded border ${styles[status] || styles.pending}`}>
        {labels[status] || status}
      </span>
    );
  };

  const getProviderStatusBadge = (status) => {
    const styles = {
      created: 'bg-blue-500/20 text-blue-400',
      confirmed: 'bg-green-500/20 text-green-400',
      picked_up: 'bg-yellow-500/20 text-yellow-400',
      completed: 'bg-gray-500/20 text-gray-400',
      cancelled: 'bg-red-500/20 text-red-400'
    };
    return (
      <span className={`text-xs px-2 py-0.5 rounded ${styles[status] || 'bg-gray-500/20 text-gray-400'}`}>
        {status || 'N/A'}
      </span>
    );
  };

  return (
    <div className="min-h-screen bg-background flex admin-theme">
      <AdminSidebar />
      
      <main className="flex-1 p-6 lg:p-8 overflow-auto">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
            <div>
              <h1 className="text-2xl font-bold text-foreground" data-testid="bookings-title">Bookings</h1>
              <p className="text-muted-foreground">{total} bookings</p>
            </div>
            <div className="flex gap-2">
              {selectedForDelete.length > 0 && (
                <Button 
                  onClick={() => setShowDeleteModal(true)} 
                  variant="destructive" 
                  size="sm"
                  data-testid="bulk-delete-btn"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete ({selectedForDelete.length})
                </Button>
              )}
              <Button onClick={fetchBookings} variant="outline" size="sm">
                <RefreshCw className="h-4 w-4 mr-2" />
                Refresh
              </Button>
            </div>
          </div>

          {/* Filters */}
          <Card className="bg-secondary border-border mb-6">
            <CardContent className="p-4">
              <form onSubmit={handleSearch} className="flex flex-col sm:flex-row gap-4">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search by ref, email or name..."
                    className="pl-10 bg-muted/30 border-border"
                    data-testid="booking-search-input"
                  />
                </div>
                <select
                  value={statusFilter}
                  onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
                  className="px-3 py-2 bg-muted/30 border border-border rounded-lg text-foreground"
                  data-testid="status-filter"
                >
                  <option value="">All Statuses</option>
                  <option value="confirmed">Confirmed</option>
                  <option value="confirmed_cash">Cash Confirmed</option>
                  <option value="pending">Pending</option>
                  <option value="cancel_requested">Cancel Requested</option>
                  <option value="cancelled">Cancelled</option>
                  <option value="cancellation_failed">Cancel Failed</option>
                  <option value="completed">Completed</option>
                </select>
                <Button type="submit" data-testid="filter-btn">
                  <Filter className="h-4 w-4 mr-2" />
                  Filter
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* Bookings Table */}
          <Card className="bg-secondary border-border">
            <CardContent className="p-0">
              {isLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-8 w-8 animate-spin text-primary" />
                </div>
              ) : bookings.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  No bookings found
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-muted/30 border-b border-border">
                      <tr>
                        <th className="p-4 w-10">
                          <input
                            type="checkbox"
                            checked={selectedForDelete.length === bookings.length && bookings.length > 0}
                            onChange={handleSelectAll}
                            className="w-4 h-4 rounded border-border bg-muted cursor-pointer"
                            data-testid="select-all-checkbox"
                          />
                        </th>
                        <th className="text-left p-4 text-sm font-medium text-muted-foreground">Ref</th>
                        <th className="text-left p-4 text-sm font-medium text-muted-foreground">Customer</th>
                        <th className="text-left p-4 text-sm font-medium text-muted-foreground">Route</th>
                        <th className="text-left p-4 text-sm font-medium text-muted-foreground">Date/Time</th>
                        <th className="text-left p-4 text-sm font-medium text-muted-foreground">Amount</th>
                        <th className="text-left p-4 text-sm font-medium text-muted-foreground">Status</th>
                        <th className="text-left p-4 text-sm font-medium text-muted-foreground">Provider</th>
                        <th className="text-left p-4 text-sm font-medium text-muted-foreground"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {bookings.map((booking) => (
                        <tr key={booking.booking_ref} className="border-b border-border hover:bg-muted/20" data-testid={`booking-row-${booking.booking_ref}`}>
                          <td className="p-4">
                            <input
                              type="checkbox"
                              checked={selectedForDelete.includes(booking.booking_ref)}
                              onChange={() => handleSelectForDelete(booking.booking_ref)}
                              className="w-4 h-4 rounded border-border bg-muted cursor-pointer"
                              data-testid={`select-booking-${booking.booking_ref}`}
                            />
                          </td>
                          <td className="p-4">
                            <span className="font-mono font-medium text-foreground">{booking.booking_ref}</span>
                          </td>
                          <td className="p-4">
                            <div className="text-sm text-foreground">{booking.customer_name || '-'}</div>
                            <div className="text-xs text-muted-foreground">{booking.customer_email}</div>
                          </td>
                          <td className="p-4">
                            <div className="text-sm text-foreground max-w-[220px]">
                              <div className="flex items-center gap-1.5">
                                {booking.pickup_type === 'airport' ? (
                                  <Plane className="h-3.5 w-3.5 text-primary flex-shrink-0" />
                                ) : (
                                  <MapPin className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
                                )}
                                <span className="truncate" title={booking.pickup_location}>
                                  {getPickupDisplayLabel(fromLegacy(booking))}
                                </span>
                              </div>
                            </div>
                            <div className="text-xs text-primary flex items-center gap-1 mt-0.5">
                              <span>→</span>
                              <span className="truncate" title={booking.dropoff_location}>
                                {getDropoffDisplayLabel(fromLegacy(booking))}
                              </span>
                            </div>
                          </td>
                          <td className="p-4">
                            <div className="text-sm text-foreground">{booking.trip_date}</div>
                            <div className="text-xs text-muted-foreground">{booking.trip_time}</div>
                          </td>
                          <td className="p-4">
                            <span className="font-semibold text-primary">{formatCurrency(booking.total)}</span>
                          </td>
                          <td className="p-4">{getStatusBadge(booking.booking_status || booking.status)}</td>
                          <td className="p-4">
                            <div className="text-xs">
                              {booking.provider_trip_id || booking.zuppin_trip_id ? (
                                <span className="text-green-400 font-mono">#{booking.provider_trip_id || booking.zuppin_trip_id}</span>
                              ) : (
                                <span className="text-muted-foreground">-</span>
                              )}
                            </div>
                            {getProviderStatusBadge(booking.provider_status || booking.zuppin_status)}
                          </td>
                          <td className="p-4">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => fetchBookingDetail(booking.booking_ref)}
                              data-testid={`view-booking-${booking.booking_ref}`}
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between p-4 border-t border-border">
                  <span className="text-sm text-muted-foreground">
                    Page {page} of {totalPages}
                  </span>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(p => Math.max(1, p - 1))}
                      disabled={page === 1}
                    >
                      Previous
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                      disabled={page === totalPages}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </main>

      {/* Booking Detail Modal */}
      {showDetail && selectedBooking && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <Card className="bg-secondary border-border w-full max-w-3xl max-h-[90vh] overflow-auto">
            <CardHeader className="flex flex-row items-center justify-between sticky top-0 bg-secondary z-10 border-b border-border">
              <CardTitle className="text-lg text-foreground">
                Booking: {selectedBooking.booking_ref}
              </CardTitle>
              <Button variant="ghost" size="sm" onClick={() => setShowDetail(false)}>
                <X className="h-5 w-5" />
              </Button>
            </CardHeader>
            <CardContent className="space-y-6 pt-4">
              {/* Status Row */}
              <div className="flex items-center gap-4 flex-wrap">
                {getStatusBadge(selectedBooking.booking_status || selectedBooking.status)}
                <span className="text-sm text-muted-foreground">
                  Created: {formatDateTime(selectedBooking.created_at)}
                </span>
              </div>

              {/* Provider Info - UPDATED */}
              <div className="p-4 bg-muted/30 rounded-lg">
                <h4 className="font-semibold text-foreground mb-3 flex items-center gap-2">
                  <Truck className="h-4 w-4 text-primary" />
                  Licensed 3rd Party Provider
                </h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-muted-foreground">Provider Trip ID:</span>
                    <span className="ml-2 text-green-400 font-mono">
                      {providerTripId || selectedBooking.provider_trip_id || selectedBooking.zuppin_trip_id || (
                        <span className="text-red-400">MISSING</span>
                      )}
                    </span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Provider Status:</span>
                    <span className="ml-2">{getProviderStatusBadge(selectedBooking.provider_status || selectedBooking.zuppin_status)}</span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Booking Status:</span>
                    <span className="ml-2">{getStatusBadge(selectedBooking.booking_status || selectedBooking.status)}</span>
                  </div>
                  
                  {/* Manual Status Update */}
                  <div className="mt-3 p-3 bg-muted/30 rounded-lg">
                    <label className="block text-sm font-medium text-foreground mb-2">
                      Update Status Manually:
                    </label>
                    <div className="flex gap-2">
                      <select
                        value={selectedBooking.booking_status || selectedBooking.status || ''}
                        onChange={(e) => handleStatusUpdate(e.target.value)}
                        disabled={isUpdatingStatus}
                        className="flex-1 px-3 py-2 bg-muted/30 border border-border rounded-lg text-foreground text-sm"
                        data-testid="status-update-select"
                      >
                        <option value="pending">Pending</option>
                        <option value="confirmed">Confirmed</option>
                        <option value="confirmed_cash">Confirmed (Cash)</option>
                        <option value="paid_confirmed">Paid & Confirmed</option>
                        <option value="in_progress">In Progress</option>
                        <option value="completed">Completed</option>
                        <option value="cancelled">Cancelled</option>
                        <option value="cancellation_failed">Cancellation Failed</option>
                      </select>
                      {isUpdatingStatus && <Loader2 className="h-5 w-5 animate-spin text-primary" />}
                    </div>
                  </div>
                  {selectedBooking.cancel_retry_count > 0 && (
                    <div>
                      <span className="text-muted-foreground">Cancel Retries:</span>
                      <span className="ml-2 text-orange-400">{selectedBooking.cancel_retry_count}/3</span>
                    </div>
                  )}
                </div>
                
                {/* Cancel blocked warning */}
                {!canCancel && cancelBlockedReason && (
                  <div className="mt-3 p-3 bg-red-500/10 border border-red-500/30 rounded-lg flex items-start gap-2">
                    <AlertTriangle className="h-4 w-4 text-red-400 mt-0.5 flex-shrink-0" />
                    <span className="text-sm text-red-400">{cancelBlockedReason}</span>
                  </div>
                )}
              </div>

              {/* Route Info - Enhanced Pickup Display */}
              <div className="p-4 bg-muted/30 rounded-lg">
                <h4 className="font-semibold text-foreground mb-3 flex items-center gap-2">
                  <MapPin className="h-4 w-4 text-primary" />
                  Transfer Details
                </h4>
                
                {/* Pickup Information - Expanded */}
                <div className="mb-4 p-3 bg-background/50 rounded-lg border border-border">
                  <div className="flex items-center gap-2 mb-2">
                    {selectedBooking.pickup_type === 'airport' ? (
                      <Plane className="h-4 w-4 text-primary" />
                    ) : selectedBooking.pickup_type === 'custom' ? (
                      <Building className="h-4 w-4 text-blue-400" />
                    ) : (
                      <MapPin className="h-4 w-4 text-muted-foreground" />
                    )}
                    <span className="font-medium text-foreground">Pickup Location</span>
                    <span className={`text-xs px-2 py-0.5 rounded ${
                      selectedBooking.pickup_type === 'airport' 
                        ? 'bg-primary/20 text-primary' 
                        : selectedBooking.pickup_type === 'custom'
                        ? 'bg-blue-500/20 text-blue-400'
                        : 'bg-muted text-muted-foreground'
                    }`}>
                      {getPickupSourceLabel(fromLegacy(selectedBooking))}
                    </span>
                  </div>
                  
                  <div className="space-y-1.5 text-sm">
                    {/* Full Address */}
                    <div>
                      <span className="text-muted-foreground">Address:</span>
                      <span className="ml-2 text-foreground">{selectedBooking.pickup_location || '-'}</span>
                    </div>
                    
                    {/* Airport Code if applicable */}
                    {selectedBooking.pickup_type === 'airport' && selectedBooking.pickup_airport_code && (
                      <div>
                        <span className="text-muted-foreground">Airport Code:</span>
                        <span className="ml-2 text-primary font-mono font-semibold">{selectedBooking.pickup_airport_code}</span>
                      </div>
                    )}
                    
                    {/* Coordinates */}
                    {(() => {
                      const coords = getCoordinates(fromLegacy(selectedBooking), 'pickup');
                      if (!coords) return null;
                      return (
                        <div className="flex items-center gap-4">
                          <div>
                            <span className="text-muted-foreground">Coordinates:</span>
                            <span className="ml-2 text-foreground font-mono text-xs">
                              {coords.lat}, {coords.lng}
                            </span>
                          </div>
                          {/* Google Maps Link */}
                          <a
                            href={getGoogleMapsUrl(coords)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-1 text-xs text-primary hover:underline"
                          >
                            <ExternalLink className="h-3 w-3" />
                            View on Maps
                          </a>
                        </div>
                      );
                    })()}
                    
                    {/* Place ID if from Google Places */}
                    {selectedBooking.pickup_place_id && (
                      <div>
                        <span className="text-muted-foreground">Place ID:</span>
                        <span className="ml-2 text-foreground font-mono text-xs">{selectedBooking.pickup_place_id}</span>
                      </div>
                    )}
                  </div>
                </div>
                
                {/* Dropoff Information */}
                <div className="mb-4 p-3 bg-background/50 rounded-lg border border-border">
                  <div className="flex items-center gap-2 mb-2">
                    <MapPin className="h-4 w-4 text-green-400" />
                    <span className="font-medium text-foreground">Dropoff Location</span>
                  </div>
                  
                  <div className="space-y-1.5 text-sm">
                    <div>
                      <span className="text-muted-foreground">Address:</span>
                      <span className="ml-2 text-foreground">{selectedBooking.dropoff_location || '-'}</span>
                    </div>
                    
                    {(() => {
                      const coords = getCoordinates(fromLegacy(selectedBooking), 'dropoff');
                      if (!coords) return null;
                      return (
                        <div className="flex items-center gap-4">
                          <div>
                            <span className="text-muted-foreground">Coordinates:</span>
                            <span className="ml-2 text-foreground font-mono text-xs">
                              {coords.lat}, {coords.lng}
                            </span>
                          </div>
                          <a
                            href={getGoogleMapsUrl(coords)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-1 text-xs text-primary hover:underline"
                          >
                            <ExternalLink className="h-3 w-3" />
                            View on Maps
                          </a>
                        </div>
                      );
                    })()}
                  </div>
                </div>

                {/* Trip Details */}
                <div className="space-y-2 text-sm">
                  <div className="flex gap-6">
                    <div>
                      <span className="text-muted-foreground">Date:</span>
                      <span className="ml-2 text-foreground">{selectedBooking.trip_date}</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Time:</span>
                      <span className="ml-2 text-foreground">{selectedBooking.trip_time}</span>
                    </div>
                  </div>
                  <div className="flex gap-6">
                    <div>
                      <span className="text-muted-foreground">Passengers:</span>
                      <span className="ml-2 text-foreground">{selectedBooking.passengers}</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Luggage:</span>
                      <span className="ml-2 text-foreground">{selectedBooking.luggage}</span>
                    </div>
                  </div>
                  {selectedBooking.vehicle_name && (
                    <div>
                      <span className="text-muted-foreground">Vehicle:</span>
                      <span className="ml-2 text-foreground">{selectedBooking.vehicle_name}</span>
                    </div>
                  )}
                  {selectedBooking.flight_number && (
                    <div>
                      <span className="text-muted-foreground">Flight No:</span>
                      <span className="ml-2 text-foreground">{selectedBooking.flight_number}</span>
                    </div>
                  )}
                  
                  {/* Route Map Link */}
                  {(() => {
                    const normalizedBooking = fromLegacy(selectedBooking);
                    const pickupCoords = getCoordinates(normalizedBooking, 'pickup');
                    const dropoffCoords = getCoordinates(normalizedBooking, 'dropoff');
                    const routeUrl = getGoogleMapsDirectionsUrl(pickupCoords, dropoffCoords);
                    
                    if (!routeUrl) return null;
                    
                    return (
                      <div className="pt-2 mt-2 border-t border-border">
                        <a
                          href={routeUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-2 px-3 py-1.5 bg-primary/10 text-primary rounded-lg text-sm hover:bg-primary/20 transition-colors"
                        >
                          <MapPin className="h-4 w-4" />
                          View Full Route on Google Maps
                          <ExternalLink className="h-3 w-3" />
                        </a>
                      </div>
                    );
                  })()}
                </div>
              </div>

              {/* Customer Info */}
              <div className="p-4 bg-muted/30 rounded-lg">
                <h4 className="font-semibold text-foreground mb-2 flex items-center gap-2">
                  <User className="h-4 w-4 text-primary" />
                  Customer Details
                </h4>
                <div className="space-y-2 text-sm">
                  <div>
                    <span className="text-muted-foreground">Name:</span>
                    <span className="ml-2 text-foreground">{selectedBooking.customer_name}</span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Email:</span>
                    <span className="ml-2 text-foreground">{selectedBooking.customer_email}</span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Phone:</span>
                    <span className="ml-2 text-foreground">{selectedBooking.customer_phone}</span>
                  </div>
                  {selectedBooking.customer_notes && (
                    <div>
                      <span className="text-muted-foreground">Notes:</span>
                      <span className="ml-2 text-foreground">{selectedBooking.customer_notes}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Payment Info */}
              <div className="p-4 bg-muted/30 rounded-lg">
                <h4 className="font-semibold text-foreground mb-2 flex items-center gap-2">
                  <CreditCard className="h-4 w-4 text-primary" />
                  Payment Details
                </h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-muted-foreground">Method:</span>
                    <span className="ml-2 text-foreground uppercase">{selectedBooking.payment_method}</span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Status:</span>
                    <span className="ml-2 text-foreground">{selectedBooking.payment_status}</span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Subtotal:</span>
                    <span className="ml-2 text-foreground">{formatCurrency(selectedBooking.subtotal)}</span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Discount:</span>
                    <span className="ml-2 text-foreground">{formatCurrency(selectedBooking.discount)}</span>
                  </div>
                  <div className="col-span-2">
                    <span className="text-muted-foreground">Total:</span>
                    <span className="ml-2 text-2xl font-bold text-primary">{formatCurrency(selectedBooking.total)}</span>
                  </div>
                </div>
                
                {/* Refund Info */}
                {selectedBooking.refund_status && (
                  <div className="mt-4 pt-4 border-t border-border">
                    <h5 className="font-medium text-foreground mb-2">Refund Status</h5>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="text-muted-foreground">Status:</span>
                        <span className={`ml-2 ${selectedBooking.refund_status === 'completed' ? 'text-green-400' : selectedBooking.refund_status === 'failed' ? 'text-red-400' : 'text-yellow-400'}`}>
                          {selectedBooking.refund_status?.toUpperCase()}
                        </span>
                      </div>
                      {selectedBooking.refund_id && (
                        <div>
                          <span className="text-muted-foreground">Refund ID:</span>
                          <span className="ml-2 text-foreground font-mono text-xs">{selectedBooking.refund_id}</span>
                        </div>
                      )}
                      {selectedBooking.refund_amount > 0 && (
                        <div>
                          <span className="text-muted-foreground">Refund Amount:</span>
                          <span className="ml-2 text-green-400">{formatCurrency(selectedBooking.refund_amount)}</span>
                        </div>
                      )}
                      {selectedBooking.refunded_at && (
                        <div>
                          <span className="text-muted-foreground">Refunded At:</span>
                          <span className="ml-2 text-foreground">{formatDateTime(selectedBooking.refunded_at)}</span>
                        </div>
                      )}
                      {selectedBooking.refund_error && (
                        <div className="col-span-2 mt-2 p-2 bg-red-500/10 border border-red-500/30 rounded text-xs text-red-400">
                          Error: {selectedBooking.refund_error}
                        </div>
                      )}
                    </div>
                  </div>
                )}
                
                {/* Manual Refund Button */}
                {canRefund && selectedBooking.booking_status === 'cancelled' && !selectedBooking.refund_status && (
                  <div className="mt-4 pt-4 border-t border-border">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleManualRefund}
                      disabled={isRefunding}
                      className="border-green-500/50 text-green-400 hover:bg-green-500/10"
                    >
                      {isRefunding ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Processing...
                        </>
                      ) : (
                        <>
                          <CreditCard className="h-4 w-4 mr-2" />
                          Process Manual Refund
                        </>
                      )}
                    </Button>
                  </div>
                )}
                
                {/* Refund Blocked Reason */}
                {refundBlockedReason && (
                  <div className="mt-3 p-2 bg-yellow-500/10 border border-yellow-500/30 rounded-lg text-xs text-yellow-400">
                    {refundBlockedReason}
                  </div>
                )}
              </div>

              {/* Extras / Services */}
              {selectedBooking.extras && selectedBooking.extras.length > 0 && (
                <div className="p-4 bg-muted/30 rounded-lg">
                  <h4 className="font-semibold text-foreground mb-3 flex items-center gap-2">
                    <Package className="h-4 w-4 text-primary" />
                    Services & Extras
                  </h4>
                  <div className="space-y-2">
                    {selectedBooking.extras.map((extra, index) => (
                      <div key={index} className={`flex items-center justify-between p-2 rounded-lg ${
                        extra.is_gift || extra.is_included 
                          ? 'bg-green-500/10 border border-green-500/30' 
                          : 'bg-background/50 border border-border'
                      }`}>
                        <div className="flex items-center gap-2">
                          {extra.is_gift && (
                            <span className="text-green-400 text-xs font-semibold uppercase bg-green-500/20 px-1.5 py-0.5 rounded">
                              Gift
                            </span>
                          )}
                          <span className={extra.is_gift ? 'text-green-400 font-medium' : 'text-foreground'}>
                            {extra.name}
                          </span>
                          {extra.quantity > 1 && (
                            <span className="text-muted-foreground text-xs">(×{extra.quantity})</span>
                          )}
                          {extra.input_value && (
                            <span className="text-xs text-muted-foreground">
                              ({extra.input_value})
                            </span>
                          )}
                        </div>
                        <div className="text-right">
                          {extra.is_gift || extra.is_included ? (
                            <div className="flex items-center gap-2">
                              <span className="text-muted-foreground line-through text-sm">
                                €{extra.original_price || 15}
                              </span>
                              <span className="text-green-400 font-semibold">FREE</span>
                            </div>
                          ) : (
                            <span className="text-primary font-medium">
                              €{extra.total || (extra.unit_price * (extra.quantity || 1))}
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  {/* Extras Total */}
                  <div className="mt-3 pt-3 border-t border-border flex justify-between items-center">
                    <span className="text-muted-foreground">Extras Total:</span>
                    <span className="text-foreground font-semibold">
                      €{selectedBooking.extras_total || 0}
                    </span>
                  </div>
                </div>
              )}

              {/* Refund Logs */}
              {refundLogs.length > 0 && (
                <div className="p-4 bg-muted/30 rounded-lg">
                  <h4 className="font-semibold text-foreground mb-3 flex items-center gap-2">
                    <CreditCard className="h-4 w-4 text-purple-400" />
                    Refund Logs
                  </h4>
                  <div className="space-y-3">
                    {refundLogs.map((log, index) => (
                      <div key={index} className="p-3 bg-background/50 rounded-lg border border-border text-sm">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            {log.success ? (
                              <CheckCircle className="h-4 w-4 text-green-400" />
                            ) : (
                              <XCircle className="h-4 w-4 text-red-400" />
                            )}
                            <span className={log.success ? 'text-green-400' : 'text-red-400'}>
                              {log.success ? 'Success' : 'Failed'}
                            </span>
                            <span className="text-muted-foreground uppercase text-xs">
                              ({log.provider || 'N/A'})
                            </span>
                          </div>
                          <span className="text-xs text-muted-foreground">
                            {formatDateTime(log.created_at)}
                          </span>
                        </div>
                        <div className="grid grid-cols-2 gap-2 text-xs">
                          <div>
                            <span className="text-muted-foreground">Amount:</span>
                            <span className="ml-1 text-foreground">{formatCurrency(log.amount)}</span>
                          </div>
                          {log.refund_id && (
                            <div>
                              <span className="text-muted-foreground">Refund ID:</span>
                              <span className="ml-1 text-foreground font-mono">{log.refund_id}</span>
                            </div>
                          )}
                        </div>
                        {log.error && (
                          <div className="mt-2 text-xs text-red-400">
                            Error: {log.error}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Cancellation Logs */}
              {cancellationLogs.length > 0 && (
                <div className="p-4 bg-muted/30 rounded-lg">
                  <h4 className="font-semibold text-foreground mb-3 flex items-center gap-2">
                    <FileText className="h-4 w-4 text-primary" />
                    Cancellation Logs
                  </h4>
                  <div className="space-y-3">
                    {cancellationLogs.map((log, index) => (
                      <div key={index} className="p-3 bg-background/50 rounded-lg border border-border text-sm">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            {log.success ? (
                              <CheckCircle className="h-4 w-4 text-green-400" />
                            ) : (
                              <XCircle className="h-4 w-4 text-red-400" />
                            )}
                            <span className={log.success ? 'text-green-400' : 'text-red-400'}>
                              {log.success ? 'Success' : 'Failed'}
                            </span>
                            {log.provider_code && (
                              <span className="text-muted-foreground">
                                (Code: {log.provider_code})
                              </span>
                            )}
                          </div>
                          <span className="text-xs text-muted-foreground">
                            {formatDateTime(log.request_time || log.created_at)}
                          </span>
                        </div>
                        <div className="grid grid-cols-2 gap-2 text-xs">
                          <div>
                            <span className="text-muted-foreground">Actor:</span>
                            <span className="ml-1 text-foreground capitalize">{log.actor}</span>
                          </div>
                          <div>
                            <span className="text-muted-foreground">Reason:</span>
                            <span className="ml-1 text-foreground">{log.reason}</span>
                          </div>
                        </div>
                        {log.error && (
                          <div className="mt-2 text-xs text-red-400">
                            Error: {log.error}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Actions */}
              {canCancel && !['cancelled', 'completed', 'cancellation_failed'].includes(selectedBooking.booking_status || selectedBooking.status) && (
                <div className="flex gap-3 pt-4 border-t border-border">
                  <Button
                    variant="destructive"
                    onClick={() => {
                      setCancelError(null);
                      setCancelReason('customer_request');
                      setShowCancelModal(true);
                    }}
                    data-testid="cancel-booking-btn"
                  >
                    <XCircle className="h-4 w-4 mr-2" />
                    Cancel Booking
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Cancel Confirmation Modal */}
      {showCancelModal && selectedBooking && (
        <div className="fixed inset-0 bg-black/70 z-[60] flex items-center justify-center p-4">
          <Card className="bg-secondary border-border w-full max-w-md">
            <CardHeader>
              <CardTitle className="text-lg text-foreground flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-red-400" />
                Cancel Booking
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-muted-foreground">
                This will cancel the trip with the provider. Are you sure you want to proceed?
              </p>
              
              <div className="p-3 bg-muted/30 rounded-lg">
                <p className="text-sm">
                  <span className="text-muted-foreground">Booking:</span>
                  <span className="ml-2 font-mono text-foreground">{selectedBooking.booking_ref}</span>
                </p>
                <p className="text-sm mt-1">
                  <span className="text-muted-foreground">Provider ID:</span>
                  <span className="ml-2 font-mono text-foreground">{providerTripId || 'N/A'}</span>
                </p>
              </div>
              
              <div>
                <label className="text-sm text-muted-foreground">Reason for Cancellation</label>
                <select
                  value={cancelReason}
                  onChange={(e) => setCancelReason(e.target.value)}
                  className="w-full mt-1 px-3 py-2 bg-muted/30 border border-border rounded-lg text-foreground"
                >
                  {CANCEL_REASONS.map(reason => (
                    <option key={reason.id} value={reason.id}>{reason.label}</option>
                  ))}
                </select>
              </div>
              
              {cancelError && (
                <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg flex items-start gap-2">
                  <AlertCircle className="h-4 w-4 text-red-400 mt-0.5 flex-shrink-0" />
                  <span className="text-sm text-red-400">{cancelError}</span>
                </div>
              )}
              
              <div className="flex gap-3 pt-2">
                <Button
                  variant="outline"
                  onClick={() => setShowCancelModal(false)}
                  disabled={isCancelling}
                  className="flex-1"
                >
                  Go Back
                </Button>
                <Button
                  variant="destructive"
                  onClick={handleCancelBooking}
                  disabled={isCancelling}
                  className="flex-1"
                  data-testid="confirm-cancel-btn"
                >
                  {isCancelling ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Cancelling...
                    </>
                  ) : (
                    'Confirm Cancel'
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black/70 z-[60] flex items-center justify-center p-4">
          <Card className="bg-secondary border-border w-full max-w-md">
            <CardHeader>
              <CardTitle className="text-lg text-foreground flex items-center gap-2">
                <Trash2 className="h-5 w-5 text-red-400" />
                Delete Bookings
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-muted-foreground">
                Are you sure you want to permanently delete {selectedForDelete.length} booking(s)? 
                This action cannot be undone.
              </p>
              
              <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                <p className="text-sm text-red-400 flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4" />
                  Warning: This will also delete all related records (cancellation logs, refund logs, etc.)
                </p>
              </div>
              
              <div className="p-3 bg-muted/30 rounded-lg max-h-40 overflow-y-auto">
                <p className="text-sm text-muted-foreground mb-2">Bookings to delete:</p>
                <div className="flex flex-wrap gap-2">
                  {selectedForDelete.map(ref => (
                    <span key={ref} className="font-mono text-xs bg-background px-2 py-1 rounded text-foreground">
                      {ref}
                    </span>
                  ))}
                </div>
              </div>
              
              <div className="flex gap-3 pt-2">
                <Button
                  variant="outline"
                  onClick={() => setShowDeleteModal(false)}
                  disabled={isDeleting}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  variant="destructive"
                  onClick={handleBulkDelete}
                  disabled={isDeleting}
                  className="flex-1"
                  data-testid="confirm-delete-btn"
                >
                  {isDeleting ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Deleting...
                    </>
                  ) : (
                    <>
                      <Trash2 className="h-4 w-4 mr-2" />
                      Delete {selectedForDelete.length} Booking(s)
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default AdminBookingsPage;
